import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import { useNavigate, useLocation, Navigate } from 'react-router'
import { StoresContext } from '@App/contexts'
import LoginForm from '@Scenes/LoginScene/LoginForm'
import { LoginFormType } from '@Entities/schemas/user/user-login'
import { TokenResponse } from '@Entities/auth/token-response'
import { analyticsSendIdentify, analyticsSendPage } from '@App/common/utils/analytics-utils'
import Navigation from '@App/navigation'

const LoginScene = () => {
    const {
        userStore,
        createPasswordStore,
        authStore,
        uiStore,
        popupStore,
        preloaderStore,
        rightsholderSearchStore,
        songwriterSearchStore,
    } = useContext(StoresContext)
    const navigate = useNavigate()
    const location = useLocation()
    const [isFirstStepDone, setFirstStepDone] = useState(false)
    const [mfaDetails, setMfaDetails] = useState<{ inactive: boolean; mfaType: string; mfaContactPoint: string }>({
        inactive: false,
        mfaContactPoint: '',
        mfaType: '',
    })

    const loginFunc = async (loginFormData: LoginFormType) => {
        preloaderStore.openMainPreloader(true)
        authStore
            .login(loginFormData.username, loginFormData.password)
            .then((result: TokenResponse) => {
                if (result.mfaType === 'email' || result.mfaType === 'sms') {
                    setMfaDetails({
                        inactive: result.inactive,
                        mfaType: result.mfaType,
                        mfaContactPoint: result.mfaContactPoint,
                    })
                    setFirstStepDone(true)
                }
                if (result.message === 'enroll_mfa') {
                    popupStore.setAlertData('Before logging in, you must enroll in two factor authentication.', true)
                    createPasswordStore.getValue(loginFormData.password, 'password')
                    createPasswordStore.setEnrollMfaEmail(loginFormData.username)
                    navigate('/enroll-mfa')
                }
            })
            .finally(() => {
                analyticsSendIdentify(userStore.userInfo)
                preloaderStore.openMainPreloader(false)
                return goToPortalHome()
            })
    }

    const goToPortalHome = () => {
        const userSettings = userStore.userInfo?.settings
        if (
            !(userSettings && 'showWelcomePopup' in userSettings && !userSettings.showWelcomePopup) &&
            rightsholderSearchStore.rightsholders?.totalElements === 0 &&
            songwriterSearchStore.songwriters?.totalElements === 0
        ) {
            return navigate(Navigation.newUser.root())
        }
        if (authStore.isLoggedIn) {
            return navigate('/')
        }
    }

    const mfaFunc = async (loginFormData: LoginFormType) => {
        // loginFormData.code should always exist here, but is technically optional.
        if (loginFormData.code) {
            preloaderStore.openMainPreloader(true)
            try {
                await authStore.mfaSubmit(loginFormData.code)
                analyticsSendIdentify(userStore.userInfo)
                goToPortalHome()
            } catch (ignore) {
                setFirstStepDone(false)
                popupStore.setAlertData('An error occurred trying to login you. Please contact support', true)
            } finally {
                preloaderStore.openMainPreloader(false)
            }
        }
    }

    const handleInactiveMessage = () =>
        location.search === '?inactive' &&
        popupStore.setAlertData(
            "Due to inactivity, you've been logged out. Thank you for visiting the MLC Portal!",
            false
        )

    useEffect(() => {
        analyticsSendPage('login')
        handleInactiveMessage()
        uiStore.setOnLoginPage(true)
        return () => {
            popupStore.setAlertData('', true)
        }
    }, [])

    return authStore.isLoggedIn ? (
        <Navigate to="/" />
    ) : (
        <>
            <title>Login</title>
            <main className="login-scene">
                <div className="global-container">
                    <LoginForm
                        mfaFunc={mfaFunc}
                        loginFunc={loginFunc}
                        isFirstStepDone={isFirstStepDone}
                        mfaDetails={mfaDetails}
                    />
                </div>
            </main>
        </>
    )
}

export default observer(LoginScene)
